import type { CSSProperties } from "react";

export type PopupPlacement = "top" | "bottom" | "left" | "right";

export interface PopupPositionResult {
  left: number;
  top: number;
  maxHeight?: number;
  placement: PopupPlacement;
}

type RectBounds = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

interface ViewportPositionCandidate {
  left: number;
  top: number;
  placement: PopupPlacement;
}

interface ResolveViewportPopupPositionParams {
  candidates: ViewportPositionCandidate[];
  popupWidth: number;
  popupHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  viewportPadding?: number;
  anchorRect?: DOMRect | null;
  avoidRect?: DOMRect | null;
}

interface ResolveClarificationPopoverStyleParams {
  anchorRect: DOMRect;
  avoidRect: DOMRect | null;
  popupWidth: number;
  popupHeight: number;
  viewportWidth: number;
  viewportHeight: number;
}

interface ResolveMentionPopupPositionParams {
  anchorRect: DOMRect;
  popupWidth: number;
  popupHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  viewportPadding?: number;
  gap?: number;
  minUsableHeight?: number;
}

const DEFAULT_VIEWPORT_PADDING = 8;
const DEFAULT_MENTION_GAP = 4;
const DEFAULT_MENTION_MIN_USABLE_HEIGHT = 120;

export function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function getIntersectionArea(a: RectBounds, b: RectBounds): number {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  if (right <= left || bottom <= top) return 0;
  return (right - left) * (bottom - top);
}

export function resolveViewportPopupPosition(
  params: ResolveViewportPopupPositionParams
): PopupPositionResult {
  const {
    candidates,
    popupWidth,
    popupHeight,
    viewportWidth,
    viewportHeight,
    anchorRect = null,
    avoidRect = null,
    viewportPadding = DEFAULT_VIEWPORT_PADDING,
  } = params;

  const anchorCenterX = anchorRect ? (anchorRect.left + anchorRect.right) / 2 : viewportWidth / 2;
  const anchorCenterY = anchorRect ? (anchorRect.top + anchorRect.bottom) / 2 : viewportHeight / 2;

  let best: (PopupPositionResult & { score: number }) | null = null;
  for (const candidate of candidates) {
    const left = clamp(
      candidate.left,
      viewportPadding,
      viewportWidth - popupWidth - viewportPadding
    );
    const top = clamp(
      candidate.top,
      viewportPadding,
      viewportHeight - popupHeight - viewportPadding
    );

    const overlapArea =
      avoidRect === null
        ? 0
        : getIntersectionArea(
            { left, top, right: left + popupWidth, bottom: top + popupHeight },
            {
              left: avoidRect.left,
              top: avoidRect.top,
              right: avoidRect.right,
              bottom: avoidRect.bottom,
            }
          );

    const rectCenterX = left + popupWidth / 2;
    const rectCenterY = top + popupHeight / 2;
    const distanceScore = Math.hypot(rectCenterX - anchorCenterX, rectCenterY - anchorCenterY);
    const score = overlapArea * 10000 + distanceScore;

    if (!best || score < best.score) {
      best = { left, top, placement: candidate.placement, score };
    }
  }

  return {
    left: best?.left ?? viewportPadding,
    top: best?.top ?? viewportPadding,
    placement: best?.placement ?? "bottom",
  };
}

export function resolveClarificationPopoverStyle(
  params: ResolveClarificationPopoverStyleParams
): CSSProperties {
  const { anchorRect, avoidRect, popupWidth, popupHeight, viewportWidth, viewportHeight } = params;
  const gap = 12;
  const viewportPadding = 12;

  const result = resolveViewportPopupPosition({
    popupWidth,
    popupHeight,
    viewportWidth,
    viewportHeight,
    anchorRect,
    avoidRect,
    viewportPadding,
    candidates: [
      { left: anchorRect.right + gap, top: anchorRect.top - 16, placement: "right" },
      {
        left: anchorRect.right + gap,
        top: anchorRect.bottom - popupHeight + 16,
        placement: "right",
      },
      { left: anchorRect.left - popupWidth - gap, top: anchorRect.top - 16, placement: "left" },
      {
        left: anchorRect.left - popupWidth - gap,
        top: anchorRect.bottom - popupHeight + 16,
        placement: "left",
      },
      {
        left: anchorRect.right - popupWidth,
        top: anchorRect.top - popupHeight - gap,
        placement: "top",
      },
      { left: anchorRect.left, top: anchorRect.top - popupHeight - gap, placement: "top" },
      {
        left: anchorRect.right - popupWidth,
        top: anchorRect.bottom + gap,
        placement: "bottom",
      },
      { left: anchorRect.left, top: anchorRect.bottom + gap, placement: "bottom" },
    ],
  });

  return {
    top: result.top,
    left: result.left,
  };
}

export function resolveMentionPopupPosition(
  params: ResolveMentionPopupPositionParams
): PopupPositionResult {
  const {
    anchorRect,
    popupWidth,
    popupHeight,
    viewportWidth,
    viewportHeight,
    viewportPadding = DEFAULT_VIEWPORT_PADDING,
    gap = DEFAULT_MENTION_GAP,
    minUsableHeight = DEFAULT_MENTION_MIN_USABLE_HEIGHT,
  } = params;

  const availableAbove = Math.max(0, anchorRect.top - viewportPadding - gap);
  const availableBelow = Math.max(0, viewportHeight - anchorRect.bottom - viewportPadding - gap);

  let placement: PopupPlacement = "bottom";
  if (availableBelow < minUsableHeight && availableAbove > availableBelow) {
    placement = "top";
  } else if (availableBelow < minUsableHeight && availableAbove < minUsableHeight) {
    placement = availableAbove >= availableBelow ? "top" : "bottom";
  }

  const availableSpace = placement === "top" ? availableAbove : availableBelow;
  const shouldConstrainHeight = popupHeight > availableSpace;
  const maxHeight = shouldConstrainHeight ? Math.max(0, availableSpace) : undefined;
  const renderedHeight = maxHeight !== undefined ? maxHeight : popupHeight;

  const top = placement === "top" ? anchorRect.top - gap - renderedHeight : anchorRect.bottom + gap;
  const left = clamp(
    anchorRect.left,
    viewportPadding,
    viewportWidth - popupWidth - viewportPadding
  );

  return {
    left,
    top: clamp(top, viewportPadding, viewportHeight - renderedHeight - viewportPadding),
    maxHeight,
    placement,
  };
}
